import { useState, useEffect, useRef, useCallback } from "react";
import { ChevronLeft, RefreshCw, MapPin, Navigation, Info } from "lucide-react";
import { toast } from "sonner";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { C } from "./data";
import { Serif, Label, Avatar } from "./ui";
import { getFamilyLocations, pushLocation } from "./consumerAuth";
import LocationPrimer from "./LocationPrimer";
import { getPermissionState, markPrimed, hasBeenPrimed, requestPosition } from "./locationPermission";

const CT = [-33.925, 18.424]; // Cape Town fallback centre

const relLabel = (at) => {
  if (!at) return "no location yet";
  const s = Math.floor((Date.now() - new Date(at).getTime()) / 1000);
  if (s < 60) return "just now";
  if (s < 3600) return `${Math.floor(s / 60)} min ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
};

const teardrop = (color, letter) =>
  L.divIcon({
    className: "rq-pin",
    html: `<div style="text-align:center;transform:translateY(-4px)">
      <div style="width:26px;height:26px;background:${color};border:3px solid #fff;border-radius:50% 50% 50% 0;transform:rotate(-45deg);box-shadow:0 2px 8px rgba(0,0,0,.35);margin:0 auto">
        <span style="display:block;transform:rotate(45deg);color:#fff;font:700 12px/20px Arial;text-align:center">${letter}</span>
      </div></div>`,
    iconSize: [26, 30],
    iconAnchor: [13, 30],
  });

export default function FamilyLocate({ nav, user, onToggleShare }) {
  const sharing = !!user?.share_location;
  const [members, setMembers] = useState([]);
  const [myLoc, setMyLoc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [primer, setPrimer] = useState(null); // null | "primer" | "denied"
  const mapRef = useRef(null);
  const layerRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const d = await getFamilyLocations();
      setMembers(d.members || []);
    } catch (e) { console.error("Family locations load failed", e); } finally { setLoading(false); }
  }, []);

  // poll family locations
  useEffect(() => {
    load();
    const id = setInterval(load, 15000);
    return () => clearInterval(id);
  }, [load]);

  // my own location (for the "You" pin)
  useEffect(() => {
    if (!navigator.geolocation) return;
    const id = navigator.geolocation.watchPosition(
      (p) => setMyLoc({ lat: p.coords.latitude, lng: p.coords.longitude }),
      () => {}, { enableHighAccuracy: true, maximumAge: 10000, timeout: 20000 }
    );
    return () => navigator.geolocation.clearWatch(id);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // init map once
  useEffect(() => {
    if (mapRef.current) return;
    const map = L.map("family-map", { zoomControl: true }).setView(CT, 11);
    L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
      maxZoom: 19, attribution: '&copy; OpenStreetMap contributors',
    }).addTo(map);
    layerRef.current = L.layerGroup().addTo(map);
    mapRef.current = map;
    setTimeout(() => map.invalidateSize(), 200);
    return () => { if (mapRef.current) { mapRef.current.remove(); mapRef.current = null; layerRef.current = null; } };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // draw pins
  useEffect(() => {
    const map = mapRef.current, layer = layerRef.current;
    if (!map || !layer) return;
    layer.clearLayers();
    const pts = [];
    if (myLoc) {
      L.marker([myLoc.lat, myLoc.lng], { icon: teardrop(C.red, "You"[0]) }).addTo(layer).bindTooltip("You", { permanent: true, direction: "top", offset: [0, -28], className: "rq-tip" });
      pts.push([myLoc.lat, myLoc.lng]);
    }
    members.forEach((m) => {
      if (m.sharing && m.location) {
        const first = (m.name || "?").split(" ")[0];
        L.marker([m.location.lat, m.location.lng], { icon: teardrop(C.green, first[0]) })
          .addTo(layer)
          .bindTooltip(first, { permanent: true, direction: "top", offset: [0, -28], className: "rq-tip" })
          .bindPopup(`<b>${first}</b><br>${m.place || "Location shared"}`);
        pts.push([m.location.lat, m.location.lng]);
      }
    });
    if (pts.length === 1) map.setView(pts[0], 14);
    else if (pts.length > 1) map.fitBounds(pts, { padding: [40, 40], maxZoom: 15 });
  }, [members, myLoc]);

  const enableSharing = async () => {
    onToggleShare && onToggleShare(true);
    try {
      const p = await requestPosition();
      pushLocation({ lat: +p.lat.toFixed(6), lng: +p.lng.toFixed(6), accuracy: Math.round(p.accuracy || 0) }).catch(() => {});
    } catch (e) {
      if (e && e.code === 1) { onToggleShare && onToggleShare(false); setPrimer("denied"); }
    }
  };

  const toggleShare = async () => {
    if (sharing) { onToggleShare && onToggleShare(false); return; }
    if (!navigator.geolocation) { toast.error("Location isn't available on this device"); return; }
    const state = await getPermissionState();
    if (state === "denied") { setPrimer("denied"); return; }
    if (state === "granted") { enableSharing(); return; }
    if (!hasBeenPrimed()) { setPrimer("primer"); return; }
    enableSharing();
  };

  const onPrimerEnable = () => { markPrimed(); setPrimer(null); enableSharing(); };

  const sharingMembers = members.filter((m) => m.sharing);
  const notSharing = members.filter((m) => !m.sharing);

  return (
    <div style={{ padding: "24px 22px 30px" }} data-testid="consumer-locate">
      <style>{`.rq-tip{background:rgba(0,0,0,.6);border:0;color:#fff;font:600 10px Arial;padding:2px 6px;border-radius:4px}.rq-tip:before{display:none}.leaflet-container{font-family:inherit}`}</style>
      <button onClick={() => nav("dependents")} data-testid="locate-back" style={{ display: "inline-flex", alignItems: "center", gap: 5, background: "none", border: 0, color: C.slate, fontSize: 13, cursor: "pointer", marginBottom: 16 }}>
        <ChevronLeft size={16} /> Household
      </button>
      <Label>Family location</Label>
      <Serif size={26} style={{ margin: "5px 0 4px" }}>Where{"'"}s <em style={{ color: C.slate }}>everyone</em>?</Serif>
      <div style={{ fontSize: 12, color: C.slate, marginBottom: 12 }}>Live location for family members who share it with you.</div>

      <div style={{ display: "flex", gap: 9, padding: "11px 13px", background: `${C.navy}0a`, border: `1px solid ${C.navy}1f`, borderRadius: 11, marginBottom: 14 }} data-testid="locate-app-open-notice">
        <Info size={15} color={C.navy} style={{ flexShrink: 0, marginTop: 1 }} />
        <div style={{ fontSize: 11.5, color: C.ink, lineHeight: 1.55, minWidth: 0 }}>
          Live location works while ResQ1 is <b>open, or running in the background</b>. If someone closes
          the app completely we can{"'"}t see where they are — you{"'"}ll see their last known spot and
          the time it was recorded.
        </div>
      </div>

      {/* Real map */}
      <div id="family-map" data-testid="locate-map" style={{ height: 260, borderRadius: 14, overflow: "hidden", border: `1px solid ${C.navy}22`, marginBottom: 12, background: "#e8ecef" }} />

      {/* Share my location toggle */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "12px 14px", background: "#fff", border: `1px solid ${sharing ? C.green : "rgba(26,30,36,0.1)"}`, borderRadius: 12, marginBottom: 8 }} data-testid="locate-share-toggle">
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <Navigation size={18} color={sharing ? C.green : C.slate} />
          <div>
            <div style={{ fontSize: 14, fontWeight: 600, color: C.ink }}>Share my live location</div>
            <div style={{ fontSize: 11, color: C.slate, maxWidth: 210, lineHeight: 1.45 }} data-testid="locate-share-hint">{sharing ? "Family can see you while ResQ1 is open or in the background — not once you close it" : "Turn on so family can find you while the app is open"}</div>
          </div>
        </div>
        <button onClick={toggleShare} data-testid="locate-share-btn"
          style={{ width: 46, height: 26, borderRadius: 999, border: 0, background: sharing ? C.green : "rgba(26,30,36,0.18)", position: "relative", cursor: "pointer", transition: "background 0.2s", flexShrink: 0 }}>
          <span style={{ position: "absolute", top: 3, left: sharing ? 23 : 3, width: 20, height: 20, borderRadius: "50%", background: "#fff", transition: "left 0.2s" }} />
        </button>
      </div>

      <button onClick={load} data-testid="locate-refresh"
        style={{ width: "100%", padding: 12, background: C.navy, color: "#fff", border: 0, borderRadius: 12, fontSize: 14, fontWeight: 600, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: 8, marginBottom: 20 }}>
        <RefreshCw size={16} /> Refresh locations
      </button>

      {loading && members.length === 0 && <div style={{ fontSize: 13, color: C.slate }}>Loading household…</div>}

      {sharingMembers.length > 0 && <Label style={{ marginBottom: 10 }}>Sharing with you</Label>}
      {sharingMembers.map((m) => (
        <div key={m.id} data-testid={`locate-member-${m.id}`}
          style={{ display: "flex", alignItems: "center", gap: 12, padding: 13, background: "#fff", border: "1px solid rgba(26,30,36,0.07)", borderRadius: 11, marginBottom: 7 }}>
          <Avatar name={m.name} color={C.green} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 14, fontWeight: 600, color: C.ink }}>{m.name}</div>
            <div style={{ fontSize: 12, color: C.ink, display: "flex", alignItems: "center", gap: 5, marginTop: 2 }}>
              <MapPin size={12} color={C.red} /> {m.place || "Location shared"}
            </div>
            <div style={{ fontSize: 11, color: C.slate, marginTop: 2 }}>
              {m.relationship} · <span style={{ fontFamily: "'JetBrains Mono', monospace" }}>{relLabel(m.last_seen)}</span>
            </div>
          </div>
          <span style={{ width: 8, height: 8, borderRadius: "50%", background: C.green }} />
        </div>
      ))}

      {notSharing.length > 0 && <Label style={{ margin: "16px 0 10px" }}>Not sharing</Label>}
      {notSharing.map((m) => (
        <div key={m.id} style={{ display: "flex", alignItems: "center", gap: 12, padding: 13, background: C.cream, borderRadius: 11, marginBottom: 7 }} data-testid={`locate-off-${m.id}`}>
          <Avatar name={m.name} color={C.slate} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 14, fontWeight: 600, color: C.ink }}>{m.name}</div>
            <div style={{ fontSize: 11, color: C.slate, lineHeight: 1.45 }} data-testid={`locate-off-hint-${m.id}`}>
              Sharing off — ask them to switch on &quot;Share my live location&quot; in their own ResQ1 app
            </div>
          </div>
        </div>
      ))}

      {!loading && members.length === 0 && (
        <div style={{ padding: 16, background: C.cream, borderRadius: 11, fontSize: 13, color: C.slate }} data-testid="locate-empty">
          No family members yet. Invite your household so you can see each other on the map.
        </div>
      )}

      {primer && <LocationPrimer variant={primer} onEnable={onPrimerEnable} onClose={() => setPrimer(null)} />}
    </div>
  );
}
